import {
  chakra,
  IconButton,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { InfoAlert } from '~components/ui/Alert';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import {
  ScheduleTimeBlockInput,
  TimeBlockCategory,
  TimeBlockType,
} from '~graphql/__generated__/types';
import { AppOption, PlaylistOption, useEditScheduleTimeBlocks } from './useEditScheduleTimeBlocks';

/**
 * Notices shown above the form, by track. Settings slots carry three because they
 * behave differently from content slots on a device that is in standby.
 */
const CATEGORY_NOTICES: Record<TimeBlockCategory, string[]> = {
  [TimeBlockCategory.Content]: ['addTimeSlotNotice'],
  [TimeBlockCategory.Settings]: [
    'addTimeSlotNoticeSettingsStandby',
    'addTimeSlotNoticeSettingsApplied',
    'addTimeSlotNoticeSettingsBrightness',
  ],
};

/**
 * Types whose notices replace the category default. Bookmark carries a second,
 * type-specific caveat in the design.
 */
const TYPE_NOTICES: Partial<Record<TimeBlockType, string[]>> = {
  [TimeBlockType.Bookmark]: ['schedulePowerOnNotice', 'addTimeSlotNoticeBookmarkLastSlot'],
  [TimeBlockType.Playlist]: ['schedulePowerOnNotice'],
};

const noticesFor = (category: TimeBlockCategory, type: TimeBlockType) =>
  TYPE_NOTICES[type] ?? CATEGORY_NOTICES[category];

interface Props {
  timeBlocks: ScheduleTimeBlockInput[];
  defaultCategory: TimeBlockCategory;
  defaultType?: TimeBlockType;
  playlists: PlaylistOption[];
  inputSources: string[];
  apps: AppOption[];
  isOpen: boolean;
  onSubmit: (timeBlocks: ScheduleTimeBlockInput[], category: TimeBlockCategory) => void;
  onCancel: () => void;
}

export function AddScheduleTimeSlotModal({ isOpen, onCancel, ...contentProps }: Props) {
  return (
    <Modal isOpen={isOpen} onClose={onCancel} scrollBehavior="inside">
      <ModalOverlay />
      {/*
       * Mounted per opening and keyed by the active tab, so the form picks up the
       * tab the user opened it from rather than the one it first rendered with.
       */}
      {isOpen && (
        <AddScheduleTimeSlotModalContent
          key={`${contentProps.defaultCategory}:${contentProps.defaultType ?? ''}`}
          onCancel={onCancel}
          {...contentProps}
        />
      )}
    </Modal>
  );
}

type ContentProps = Omit<Props, 'isOpen'>;

function AddScheduleTimeSlotModalContent({
  timeBlocks,
  defaultCategory,
  defaultType,
  playlists,
  inputSources,
  apps,
  onSubmit,
  onCancel,
}: ContentProps) {
  const { handleSubmit, reset, currentValues, FormBody, SubmitButton, CancelButton } =
    useEditScheduleTimeBlocks({
      timeBlocks,
      defaultCategory,
      defaultType,
      playlists,
      inputSources,
      apps,
      defaultValues: { start: '09:00', end: '10:00', days: [] },
      onSubmit: (blocks) => onSubmit(blocks, currentValues.category),
      onCancel,
      allowSelectDays: true,
    });

  const onClose = () => {
    onCancel();
    reset();
  };

  return (
    <ModalContent as="form" onSubmit={handleSubmit} maxWidth="624px">
      <ModalHeader>{t('addNewTimeSlot')}</ModalHeader>
      <ModalCloseButton onClick={onClose} />
      {/*
       * The form runs past the fold, and an overlay scrollbar stays invisible until
       * the first scroll - so the fields below look absent and the disabled submit
       * looks like a hung dialog. Painting the track keeps the rest discoverable.
       */}
      <ModalBody
        overflowY="scroll"
        sx={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--chakra-colors-gray-300) var(--chakra-colors-gray-50)',
          // `-webkit-appearance: none` is what turns the macOS overlay scrollbar into a
          // classic one; width and colours alone leave it transparent until a scroll.
          '&::-webkit-scrollbar': { WebkitAppearance: 'none', width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'gray.50', borderRadius: 'full' },
          '&::-webkit-scrollbar-thumb': { background: 'gray.300', borderRadius: 'full' },
          '&::-webkit-scrollbar-thumb:hover': { background: 'gray.400' },
        }}
      >
        {/* Follows the selects, not the tab, so it tracks what is actually being created. */}
        <ScheduleTimeSlotNotices notices={noticesFor(currentValues.category, currentValues.type)} />
        <chakra.p marginBottom={5}>{t('addNewScheduleTimeSlotDesc')}</chakra.p>
        {FormBody}
      </ModalBody>
      <ModalFooter>
        <CancelButton />
        <SubmitButton>{t('addSlot')}</SubmitButton>
      </ModalFooter>
    </ModalContent>
  );
}

/**
 * The notices for a slot, as one alert. Several of them stacked pushed the form -
 * and the day picker the submit depends on - past the fold, so only the first is
 * shown until the reader asks for the rest. A lone notice needs no toggle.
 */
function ScheduleTimeSlotNotices({ notices }: { notices: string[] }) {
  const { isOpen, onToggle } = useDisclosure();

  if (notices.length < 2) {
    return (
      <chakra.div marginBottom={5}>
        <InfoAlert>{t(notices[0])}</InfoAlert>
      </chakra.div>
    );
  }

  return (
    <chakra.div marginBottom={5}>
      <InfoAlert
        // Centred while it is a single line; once the list is open the icon and the
        // toggle belong beside the first bullet rather than halfway down the alert.
        alignItems={isOpen ? 'flex-start' : 'center'}
        actionButton={
          <IconButton
            aria-label={t(isOpen ? 'showLess' : 'showMore')}
            variant="ghost"
            colorScheme="blue"
            size="xs"
            // Sized to the glyph so the button does not set the collapsed row height.
            height="6"
            minWidth="6"
            onClick={onToggle}
            icon={
              isOpen ? (
                <ChevronUpIcon width={5} height={5} />
              ) : (
                <ChevronDownIcon width={5} height={5} />
              )
            }
          />
        }
      >
        {isOpen ? (
          <UnorderedList spacing="2" marginLeft="4">
            {notices.map((notice) => (
              <ListItem key={notice}>{t(notice)}</ListItem>
            ))}
          </UnorderedList>
        ) : (
          <Text noOfLines={1}>{t(notices[0])}</Text>
        )}
      </InfoAlert>
    </chakra.div>
  );
}
